import styled from 'styled-components';
import { FillBar, Flex } from '@elementary/common-ui/components';
import { Tooltip, Typography } from '@mui/material';
import {
  formatNeverQueriedTableUsageTooltip,
  formatTableUsageTooltip,
  usagePercentileToLabel,
} from './tableUsageUtils';

const Label = styled(Typography)`
  font-size: 14px;
  line-height: 1.2;
`;

const TooltipTrigger = styled('span')`
  display: inline-flex;
  cursor: default;
  width: fit-content;
  max-width: 100%;
`;

const Row = styled(Flex)<{ $showLabel: boolean }>`
  min-width: 0;

  ${({ $showLabel }) =>
    $showLabel
      ? `
    gap: 10px;
    align-items: center;
  `
      : `
    width: fit-content;
    max-width: 100%;
    line-height: 0;
  `}
`;

export type TableUsageBarVariant = 'wide' | 'compact';

type Props = {
  usagePercentile: number | null;
  showLabel?: boolean;
  periodStart?: string;
  periodEnd?: string;
  queriesCount?: number;
  uniqueUserCount?: number;
  barVariant?: TableUsageBarVariant;
};

const TableUsagePopularity = ({
  usagePercentile,
  showLabel = true,
  periodStart,
  periodEnd,
  queriesCount,
  uniqueUserCount,
  barVariant = 'wide',
}: Props) => {
  const tooltipTitle =
    usagePercentile === null
      ? formatNeverQueriedTableUsageTooltip()
      : periodStart !== undefined &&
        periodEnd !== undefined &&
        queriesCount !== undefined &&
        uniqueUserCount !== undefined
      ? formatTableUsageTooltip({
          usagePercentile,
          periodStart,
          periodEnd,
          queriesCount,
          uniqueUserCount,
        })
      : undefined;

  const labelText =
    usagePercentile === null
      ? 'Never queried'
      : usagePercentileToLabel(usagePercentile);

  return (
    <Tooltip
      title={tooltipTitle}
      enterDelay={300}
      componentsProps={{
        tooltip: { sx: { whiteSpace: 'pre-line' } },
      }}
    >
      <TooltipTrigger>
        <Row $showLabel={showLabel}>
          <FillBar value={usagePercentile ?? 0} variant={barVariant} />
          {showLabel && <Label>{labelText}</Label>}
        </Row>
      </TooltipTrigger>
    </Tooltip>
  );
};

export default TableUsagePopularity;
